import { addHours } from "date-fns";
import { format as dateFnsTzFormat, formatInTimeZone, toDate } from "date-fns-tz";
import { format as dateFnsFormat } from "date-fns/format";
import { formatDistanceToNowStrict } from "date-fns/formatDistanceToNowStrict";
import { isAfter } from "date-fns/isAfter";
import { isBefore } from "date-fns/isBefore";
import { isToday } from "date-fns/isToday";
import { isTomorrow } from "date-fns/isTomorrow";
import { isYesterday } from "date-fns/isYesterday";

export namespace format {
  export function relativeDate(
    date?: Date | number | string | null,
    options?: { isShowTodayAndTomorrowAsSingleWord: boolean },
  ): string {
    if (!date) {
      return "";
    }

    let dateUsable: Date | number;
    if (typeof date === "string") {
      dateUsable = toDate(date);
    } else {
      dateUsable = date;
    }
    if (options?.isShowTodayAndTomorrowAsSingleWord) {
      if (isTomorrow(dateUsable)) {
        return "Tomorrow";
      }
      if (isToday(dateUsable)) {
        return "Today";
      }
      if (isYesterday(dateUsable)) {
        return "Yesterday";
      }
    }
    return formatDistanceToNowStrict(dateUsable, { addSuffix: true });
  }

  export function formatDateWithContext(
    date?: Date | number | string | null,
    options?: { isShowTodayAndTomorrowAsSingleWord: boolean },
    maxDaysForRelativeFormat: number | string = 5,
  ): string {
    if (!date) {
      return "";
    }

    let dateUsable: Date | number;
    if (typeof date === "string") {
      dateUsable = toDate(date);
    } else {
      dateUsable = date;
    }

    const today = new Date();

    if (options?.isShowTodayAndTomorrowAsSingleWord) {
      if (isToday(dateUsable)) {
        return "Today";
      }

      if (isTomorrow(dateUsable)) {
        return "Tomorrow";
      }

      if (isYesterday(dateUsable)) {
        return "Yesterday";
      }
    }

    const daysDifference = Math.abs(
      Math.round(
        ((typeof dateUsable === "number"
          ? new Date(new Date(dateUsable).setHours(0, 0, 0, 0)).getTime()
          : new Date(dateUsable.setHours(0, 0, 0, 0)).getTime()) -
          new Date(today.setHours(0, 0, 0, 0)).getTime()) /
          (1000 * 60 * 60 * 24),
      ),
    );

    if (daysDifference < Number(maxDaysForRelativeFormat)) {
      if (isBefore(dateUsable, today)) {
        return `${daysDifference} days ago`;
      } else if (isAfter(dateUsable, today)) {
        return `${daysDifference} days from now`;
      }
    }
    if (Number(maxDaysForRelativeFormat) == 1) {
      return dateFnsFormat(dateUsable, "MMM do");
    }

    return dateFnsFormat(dateUsable, "MMM d");
  }

  export function fullDate(date?: Date | number | string | null): string {
    if (!date) {
      return "";
    }

    let dateUsable: Date | number;
    if (typeof date === "string") {
      dateUsable = toDate(date);
    } else {
      dateUsable = date;
    }

    return dateFnsFormat(dateUsable, "MMM do yy");
  }

  export function fullDateTime(
    date?: Date | number | string | null,
    approximate?: boolean,
  ): string {
    if (!date) {
      return "";
    }

    let dateUsable: Date;
    if (typeof date === "string") {
      dateUsable = toDate(date);
    } else if (date instanceof Date) {
      dateUsable = date;
    } else {
      dateUsable = new Date(date);
    }

    if (approximate) {
      const minutes = dateUsable.getMinutes();
      if (minutes >= 30) {
        dateUsable = addHours(dateUsable, 1);
      }

      return dateFnsFormat(dateUsable, "MMM do yy, haaa");
    } else {
      return dateFnsFormat(dateUsable, "MMM do yy, h:mmaaa");
    }
  }

  export function currency(
    amount?: number | string | null,
    options?: { decimals?: number; isCompact?: boolean },
  ): string {
    // Explicit undefined check, to allow zero amount.
    if (amount === undefined || amount === null) {
      return "";
    }

    const isCompact = options?.isCompact && Number(amount) >= 1000;

    let decimals = options?.decimals ?? 0;
    if (isCompact) {
      decimals = 1;
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      notation: isCompact ? "compact" : "standard",
    })
      .format(Number(amount))
      .replace("K", "k");
  }

  export function currencyK(amount?: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      compactDisplay: "short",
    }).format(Number(amount));
  }

  export const money = currency;

  export function titleCase(str: string) {
    return str.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
  }

  export type DateRaw = Date | string | number | null | undefined;

  export function date(
    dateRaw?: DateRaw,
    options?: {
      isAlwaysShowYear?: boolean;
      isShowTime?: boolean;
      isShowWeekday?: boolean;
    },
  ): string {
    const date = parseDate(dateRaw);
    if (!date) {
      return "";
    }
    // Note: don't fall for blackbaud's handledAt, once it was "1900-01-11T10:46:49+01:36:34" ¯\_(ツ)_/¯
    if (isNaN(date.valueOf())) {
      return "Invalid Date";
    }

    const isSameYear = date.getFullYear() === new Date().getFullYear();

    const dayPart = options?.isShowWeekday ? "EEE MMM d" : "MMM d";
    const yearPart = options?.isAlwaysShowYear || !isSameYear ? ", yyyy" : "";
    const timePart = options?.isShowTime ? " h:mmaaaaa'm" : "";
    return dateFnsFormat(date, `${dayPart}${yearPart}${timePart}`);
  }

  export function dateCustom(dateRaw: DateRaw = null, format: string): string {
    const date = parseDate(dateRaw);
    if (!date) {
      return "";
    }
    return dateFnsFormat(date, format);
  }

  // CRMs that allow a birthday without a year make us store a placeholder
  // year (1, or 4 for 29 February) so the date stays valid.
  const FIRST_REAL_BIRTH_YEAR = 1000;

  function parseBirthday(value?: string | null): { date: Date; hasRealYear: boolean } | null {
    const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
    if (!match) {
      return null;
    }
    const [year, month, day] = match.slice(1).map(Number);
    const hasRealYear = year >= FIRST_REAL_BIRTH_YEAR;
    // 2000 is a leap year, so 29 February survives without its real year
    return { date: new Date(hasRealYear ? year : 2000, month - 1, day), hasRealYear };
  }

  export function birthday(value?: string | null): string {
    const parsed = parseBirthday(value);
    if (!parsed) {
      return "";
    }
    return dateFnsFormat(parsed.date, parsed.hasRealYear ? "MMM d, yyyy" : "MMM d");
  }

  export function birthdayCompact(value?: string | null): string {
    const parsed = parseBirthday(value);
    if (!parsed) {
      return "";
    }
    return dateFnsFormat(parsed.date, parsed.hasRealYear ? "MMM do yy" : "MMM do");
  }

  export function datetime(dateRaw?: DateRaw): string {
    const date = parseDate(dateRaw);
    if (!date) {
      return "";
    }

    return dateFnsTzFormat(date, "yyyy-MM-dd HH:mm:ss");
  }

  function parseDate(dateRaw?: DateRaw): Date | null {
    if (!dateRaw) {
      return null;
    }

    if (typeof dateRaw === "string") {
      return toDate(dateRaw);
    } else if (typeof dateRaw === "number") {
      return new Date(dateRaw);
    } else {
      return dateRaw;
    }
  }

  /**
   * Format a Date as `yyyy-MM-dd` in the given IANA timezone.
   *
   * `tz` is required. Server-side rec scheduling reads this value back as a
   * calendar day in the org's timezone, so callers that formatted in the
   * browser's local tz shipped recs onto the wrong calendar day whenever the
   * browser and org disagreed (e.g. Playwright's UTC browser vs a PT org
   * after ~17:00 PT). Passing `state.auth.backend.user!.org!.tz` at each
   * callsite keeps client-computed dates aligned with `startOfOrgDay()` on
   * the read path (see `state/inboxState.ts`).
   */
  export function dateISO(date: Date, tz: string): string {
    return formatInTimeZone(date, tz, "yyyy-MM-dd");
  }

  /** Whether `date` falls on today's calendar day in the org's timezone, not the browser's. */
  export function isOrgToday(date: Date, tz: string, now: Date = new Date()): boolean {
    return dateISO(date, tz) === dateISO(now, tz);
  }

  export function truncatePrompt(input: string, limit: number = 80) {
    return input.length > limit ? `${input.substring(0, limit).trim()}…` : input;
  }

  export function slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w-]+/g, "") // Remove all non-word chars
      .replace(/--+/g, "-") // Replace multiple - with single -
      .replace(/^-+/, "") // Trim - from start of text
      .replace(/-+$/, ""); // Trim - from end of text
  }
}
